import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, GripVertical, Save, Package, Ruler, ListChecks, Building2, Pencil, Network } from "lucide-react";
import { SectorsSettings } from "@/components/SectorsSettings";
import { toast } from "sonner";

const TABS = [
  { key: "categories", label: "Categorias de Materiais", icon: Package, desc: "Categorias disponiveis no cadastro de materiais do estoque" },
  { key: "units", label: "Unidades de Medida", icon: Ruler, desc: "Unidades de medida para materiais e sub-etapas" },
  { key: "default_stages", label: "Etapas Padrao", icon: ListChecks, desc: "Etapas sugeridas automaticamente ao criar nova ordem de producao" },
  { key: "companies", label: "Empresas", icon: Building2, desc: "Empresas emissoras das ordens de expedicao" },
  { key: "sectors", label: "Setores", icon: Network, desc: "Setores da empresa e permissoes padrao" },
];

export default function ConfigPage() {
  const [activeTab, setActiveTab] = useState("categories");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Companies state
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({ name: "", cnpj: "", address: "", city: "", state: "", cep: "", phone: "", email: "" });

  const fetchSettings = useCallback(async (type) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/settings/${type}`, { withCredentials: true });
      setItems(data.items || []);
      setHasChanges(false);
    } catch { toast.error("Erro ao carregar configuracoes"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (activeTab !== "sectors") fetchSettings(activeTab); }, [activeTab, fetchSettings]);

  // Company CRUD
  const fetchCompanies = useCallback(async () => {
    setCompaniesLoading(true);
    try { const { data } = await axios.get(`${API}/companies`, { withCredentials: true }); setCompanies(data); }
    catch { toast.error("Erro ao carregar empresas"); }
    finally { setCompaniesLoading(false); }
  }, []);

  useEffect(() => { if (activeTab === "companies") fetchCompanies(); }, [activeTab, fetchCompanies]);

  const openCompanyDialog = (company = null) => {
    if (company) {
      setEditingCompany(company);
      setCompanyForm({ name: company.name, cnpj: company.cnpj || "", address: company.address || "", city: company.city || "", state: company.state || "", cep: company.cep || "", phone: company.phone || "", email: company.email || "" });
    } else {
      setEditingCompany(null);
      setCompanyForm({ name: "", cnpj: "", address: "", city: "", state: "", cep: "", phone: "", email: "" });
    }
    setCompanyDialogOpen(true);
  };

  const handleSaveCompany = async () => {
    if (!companyForm.name.trim()) { toast.error("Nome da empresa e obrigatorio"); return; }
    try {
      if (editingCompany) {
        await axios.put(`${API}/companies/${editingCompany.id}`, companyForm, { withCredentials: true });
        toast.success("Empresa atualizada");
      } else {
        await axios.post(`${API}/companies`, companyForm, { withCredentials: true });
        toast.success("Empresa cadastrada");
      }
      setCompanyDialogOpen(false);
      fetchCompanies();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao salvar empresa"); }
  };

  const handleDeleteCompany = async (id) => {
    try {
      await axios.delete(`${API}/companies/${id}`, { withCredentials: true });
      toast.success("Empresa removida");
      fetchCompanies();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao excluir"); }
  };

  const handleSave = async () => {
    // Validate no empty values
    const valid = items.filter((i) => i.value.trim() && i.label.trim());
    if (valid.length === 0) {
      toast.error("Adicione pelo menos um item");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/settings/${activeTab}`, { items: valid }, { withCredentials: true });
      setItems(valid);
      setHasChanges(false);
      toast.success("Configuracoes salvas");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };

  const addItem = () => {
    setItems([...items, { value: "", label: "" }]);
    setHasChanges(true);
  };

  const removeItem = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
    setHasChanges(true);
  };

  const updateItem = (idx, field, val) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: val };
    // Auto-generate value from label if value is empty
    if (field === "label" && !updated[idx].value) {
      updated[idx].value = val.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    }
    setItems(updated);
    setHasChanges(true);
  };

  const activeTabInfo = TABS.find((t) => t.key === activeTab);

  return (
    <div className="space-y-6 animate-slide-in" data-testid="config-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Configuracoes</h1>
        <p className="text-sm text-muted-foreground mt-1">Personalize as opcoes do sistema</p>
      </div>

      <div className="flex gap-6">
        {/* Tabs sidebar */}
        <div className="w-64 shrink-0 space-y-1">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${
                activeTab === tab.key
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted"
              }`}
              data-testid={`config-tab-${tab.key}`}
            >
              <tab.icon className="h-4 w-4 shrink-0" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1">
          {activeTab === "sectors" ? <SectorsSettings /> : (
          <Card className="border border-border shadow-none">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">{activeTabInfo?.label}</CardTitle>
                  <CardDescription>{activeTabInfo?.desc}</CardDescription>
                </div>
                {activeTab !== "companies" ? (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={addItem} data-testid="config-add-item-btn">
                      <Plus className="h-3.5 w-3.5 mr-1" /> Adicionar
                    </Button>
                    <Button size="sm" onClick={handleSave} disabled={!hasChanges || saving} data-testid="config-save-btn">
                      <Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Salvando..." : "Salvar"}
                    </Button>
                  </div>
                ) : (
                  <Button size="sm" onClick={() => openCompanyDialog()} data-testid="add-company-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Nova Empresa
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {activeTab === "companies" ? (
                /* Companies tab */
                companiesLoading ? (
                  <p className="text-center text-muted-foreground py-8">Carregando...</p>
                ) : companies.length === 0 ? (
                  <div className="text-center py-8">
                    <Building2 className="h-10 w-10 mx-auto mb-2 text-muted-foreground/30" />
                    <p className="text-muted-foreground mb-3">Nenhuma empresa cadastrada</p>
                    <Button variant="outline" size="sm" onClick={() => openCompanyDialog()}>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Cadastrar primeira empresa
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {companies.map((c) => (
                      <div key={c.id} className="flex items-start justify-between p-4 border rounded-lg" data-testid={`company-${c.id}`}>
                        <div className="space-y-1">
                          <p className="font-semibold text-sm">{c.name}</p>
                          {c.cnpj && <p className="text-xs text-muted-foreground font-mono">{c.cnpj}</p>}
                          {(c.address || c.city) && <p className="text-xs text-muted-foreground">{[c.address, c.city, c.state].filter(Boolean).join(", ")}{c.cep ? ` - CEP ${c.cep}` : ""}</p>}
                          <div className="flex gap-4 text-xs text-muted-foreground">
                            {c.phone && <span>{c.phone}</span>}
                            {c.email && <span>{c.email}</span>}
                          </div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openCompanyDialog(c)} data-testid={`edit-company-${c.id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDeleteCompany(c.id)} data-testid={`delete-company-${c.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                /* Settings tabs (categories, units, stages) */
                <>
                  {loading ? (
                    <p className="text-center text-muted-foreground py-8">Carregando...</p>
                  ) : items.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground mb-3">Nenhum item cadastrado</p>
                      <Button variant="outline" size="sm" onClick={addItem}>
                        <Plus className="h-3.5 w-3.5 mr-1" /> Adicionar primeiro item
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="grid grid-cols-[auto_1fr_1fr_auto] gap-2 px-1 text-xs text-muted-foreground uppercase font-medium">
                        <div className="w-6" />
                        <div>Nome (exibicao)</div>
                        <div>Valor (interno)</div>
                        <div className="w-9" />
                      </div>
                      {items.map((item, idx) => (
                        <div key={idx} className="grid grid-cols-[auto_1fr_1fr_auto] gap-2 items-center" data-testid={`config-item-${idx}`}>
                          <GripVertical className="h-4 w-4 text-muted-foreground/40" />
                          <Input value={item.label} onChange={(e) => updateItem(idx, "label", e.target.value)} placeholder="Ex: Tubo Galvanizado" className="h-9" data-testid={`config-item-label-${idx}`} />
                          <Input value={item.value} onChange={(e) => updateItem(idx, "value", e.target.value)} placeholder="ex: tubo_galvanizado" className="h-9 font-mono text-xs" data-testid={`config-item-value-${idx}`} />
                          <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => removeItem(idx)} data-testid={`config-item-delete-${idx}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  {hasChanges && (
                    <p className="text-xs text-amber-600 mt-3">Alteracoes nao salvas. Clique em "Salvar" para aplicar.</p>
                  )}
                </>
              )}
            </CardContent>
          </Card>
          )}
        </div>
      </div>

      {/* Company Dialog */}
      <Dialog open={companyDialogOpen} onOpenChange={setCompanyDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingCompany ? "Editar Empresa" : "Nova Empresa"}</DialogTitle>
            <DialogDescription>Dados da empresa emissora</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Razao Social *</Label>
                <Input value={companyForm.name} onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })} placeholder="Razao social da empresa" data-testid="company-name-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">CNPJ</Label>
                <Input value={companyForm.cnpj} onChange={(e) => setCompanyForm({ ...companyForm, cnpj: e.target.value })} placeholder="00.000.000/0000-00" data-testid="company-cnpj-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">CEP</Label>
                <Input value={companyForm.cep} onChange={(e) => setCompanyForm({ ...companyForm, cep: e.target.value })} placeholder="00000-000" data-testid="company-cep-input" />
              </div>
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Endereco</Label>
                <Input value={companyForm.address} onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })} placeholder="Rua, numero, bairro" data-testid="company-address-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Cidade</Label>
                <Input value={companyForm.city} onChange={(e) => setCompanyForm({ ...companyForm, city: e.target.value })} placeholder="Cidade" data-testid="company-city-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Estado</Label>
                <Input value={companyForm.state} onChange={(e) => setCompanyForm({ ...companyForm, state: e.target.value })} placeholder="UF" data-testid="company-state-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Telefone</Label>
                <Input value={companyForm.phone} onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })} placeholder="(00) 0000-0000" data-testid="company-phone-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Email</Label>
                <Input value={companyForm.email} onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })} placeholder="email@empresa.com" data-testid="company-email-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompanyDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveCompany} disabled={!companyForm.name.trim()} data-testid="company-save-btn">
              {editingCompany ? "Salvar" : "Cadastrar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
